import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import api from '@/services/axios'
import type { DriverStats, FilterObject, Order } from '@/types'
import { paginationDefault } from '@/constants'
export const useAdminStore = defineStore('admin', () => {
  const metrics = ref({})
  const recentOrders = ref<Order[]>([])
  const recentPayments = ref([])
  const ordersPagination = ref(paginationDefault)
  const paymentsPagination = ref(paginationDefault)
  const usersPagination = ref(paginationDefault)
  const users = ref([])
  const feedbackPagination = ref(paginationDefault)
  const feedbacks = ref([])
  const feedback = ref({})
  const loading = ref(false)
  const error = ref(null)

  // Actions
  async function fetchDashboardData(dateFilter: string = 'today', startDate: string = '', endDate: string = '') {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/dashboard?date_filter=${dateFilter}&start_date=${startDate}&end_date=${endDate}`)

      // Update stats and current orders from the dashboard endpoint
      metrics.value = response.data.metrics
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data'
      error.value = errorMessage
      console.error('Error fetching dashboard data:', err)
    } finally {
      loading.value = false
    }
  }
  async function fetchOrders(page = 1) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/orders?page=${page}`)

      // Update stats and current orders from the dashboard endpoint
      recentOrders.value = response.data.orders
      ordersPagination.value = response.data.pagination
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data'
      error.value = errorMessage
    } finally {
      loading.value = false
    }
  }
  async function fetchPayments(page = 1) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/payments?page=${page}`)

      // Update stats and current orders from the dashboard endpoint
      recentPayments.value = response.data.payments
      paymentsPagination.value = response.data.pagination
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data'
      error.value = errorMessage
    } finally {
      loading.value = false
    }
  }
  async function fetchFeedbacks(page = 1) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/feedback?page=${page}`)

      // Update stats and current orders from the dashboard endpoint
      feedbacks.value = response.data.data
      feedbackPagination.value = {
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
        from: response.data.from,
        to: response.data.to,
        links: response.data.links
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data'
      error.value = errorMessage
    } finally {
      loading.value = false
    }
  }
  async function fetchUsers(page = 1, filterObject: FilterObject | null = null) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/users?page=${page}&search=${filterObject?.search ?? ''}&role=${filterObject?.selectedRole ?? ''}&hasBankAccount=${filterObject?.hasBankAccount.toString() ?? ''}`)
      // console.log(response.data)
      // Update stats and current orders from the dashboard endpoint
      users.value = response.data.users
      usersPagination.value = response.data.pagination
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard data'
      error.value = errorMessage
    } finally {
      loading.value = false
    }
  }

  async function fetchUserDetails(userId: string | number) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/users/${userId}`)
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load user details'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchFeedback(feedbackId: string | number) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(`/api/admin/feedback/${feedbackId}`)
      feedback.value = response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load feedback'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  // Initialize data
  function initialize() {
    fetchDashboardData()
    fetchOrders()
    fetchPayments()
    fetchUsers()
  }

  // Send bulk notifications to a specific user group
  async function sendBulkNotification(notificationData: {
    userGroup: string,
    type: string,
    title: string,
    message: string,
    action_url?: string | null,
    action_text?: string | null
  }) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/admin/notifications/send-bulk', notificationData)
      return response.data
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send notifications'
      error.value = errorMessage
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    metrics,
    recentOrders,
    recentPayments,
    ordersPagination,
    paymentsPagination,
    users,
    usersPagination,
    loading,
    error,
    fetchFeedbacks,
    feedbacks,
    feedbackPagination,
    fetchFeedback,
    feedback,
    fetchDashboardData,
    fetchOrders,
    fetchPayments,
    fetchUsers,
    fetchUserDetails,
    initialize,
    sendBulkNotification,
  }
})
